import { Theme as SemanticUITheme } from '@rjsf/semantic-ui';
import type {
  WidgetProps,
  FieldTemplateProps,
  BaseInputTemplateProps,
} from '@rjsf/utils';
import {
  getInputProps,
  labelValue,
  ariaDescribedByIds,
  examplesId,
} from '@rjsf/utils';
import _startCase from 'lodash/startCase';
import React, { useState } from 'react';
import type { ColorResult } from 'react-color';
import { ChromePicker } from 'react-color';
import { Form, Input, Divider, Button, Popup } from 'semantic-ui-react';

import { darkReaderIgnoreInlineStyleClass } from '@components/dark-mode/dark-mode-provider';

import { NullableObjectField } from './nullable-object-field';
import { NullableObjectFieldTemplate } from './nullable-object-field-template';

/**
 * Format a label to be human-readable:
 * - Converts camelCase to separate words
 * - Replaces underscores with spaces
 * - Capitalizes first letter of each word
 *
 * Examples:
 * - `emailAlerts` → `Email Alerts`
 * - `api_key` → `Api Key`
 */
const formatLabel = (label: string): string => {
  if (!label) return label;
  return _startCase(label).trim();
};

/**
 * Custom color picker widget using react-color ChromePicker for selecting
 * and displaying color values with a visual preview.
 */
export const ColorPickerWidget = (props: WidgetProps) => {
  const { value, onChange, disabled, readonly, placeholder, label, hideLabel } =
    props;
  const [showPicker, setShowPicker] = useState(false);
  const [displayColor, setDisplayColor] = useState(value || '');

  // Update display color when value changes externally
  React.useEffect(() => {
    setDisplayColor(value || '');
  }, [value]);

  // Handle color picker changes
  const handleColorChange = (color: ColorResult) => {
    let hexColor = color.hex;

    // If alpha channel is present and not fully opaque, include it in hex
    if (color.rgb.a !== undefined && color.rgb.a < 1) {
      const alpha = Math.round(color.rgb.a * 255);
      const alphaHex = alpha.toString(16).padStart(2, '0');
      hexColor = `${color.hex}${alphaHex}`;
    }

    setDisplayColor(hexColor);
    onChange(hexColor);
  };

  // Handle direct input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    data: { value: string },
  ) => {
    const inputValue = data.value.trim();
    setDisplayColor(inputValue);
    onChange(inputValue);
  };

  // Handle color picker button click with event prevention
  const handlePickerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPicker(!showPicker);
  };

  // Handle clearing the color value
  const handleClearColor = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDisplayColor('');
    onChange('');
  };

  // Determine what to show in the color preview button
  const hasColor = displayColor && displayColor.trim() !== '';
  const previewColor = hasColor ? displayColor : '#f8f8f8';
  const showColorPattern = !hasColor;

  const buttonClasses = [
    'rjsf-color-picker-preview-button',
    darkReaderIgnoreInlineStyleClass,
    disabled || readonly ? 'disabled' : '',
    showColorPattern ? 'no-color' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Format the label
  const formattedLabel = label ? formatLabel(label) : undefined;

  return (
    <div className="rjsf-color-picker-wrapper">
      <Input
        value={displayColor}
        onChange={handleInputChange}
        disabled={disabled || readonly}
        placeholder={placeholder || 'Enter color (e.g., #ff0000)'}
        className="rjsf-color-picker-input"
        label={labelValue(formattedLabel || undefined, hideLabel, false)}
      />
      <div className="rjsf-color-picker-controls">
        <Popup
          trigger={
            <Button
              type="button"
              disabled={disabled || readonly}
              className={buttonClasses}
              style={{
                backgroundColor: hasColor ? previewColor : undefined,
              }}
              onClick={handlePickerClick}
              title={
                hasColor ? `Color: ${displayColor}` : 'Click to select color'
              }
            />
          }
          content={
            <div>
              <ChromePicker
                color={displayColor || '#ffffff'}
                onChange={handleColorChange}
                onChangeComplete={handleColorChange}
              />
            </div>
          }
          on="click"
          open={showPicker}
          onClose={() => setShowPicker(false)}
          position="bottom left"
          basic
          flowing
          hoverable={false}
        />
        {hasColor && !disabled && !readonly && (
          <Button
            type="button"
            icon="remove"
            size="small"
            onClick={handleClearColor}
            title="Clear color"
            className="rjsf-color-picker-clear-button"
          />
        )}
      </div>
    </div>
  );
};

/**
 * Custom BaseInputTemplate that formats labels to initial-case and removes
 * underscores. This wraps the built-in semantic-ui input.
 */
export const BaseInputTemplate = (props: BaseInputTemplateProps) => {
  const {
    id,
    placeholder,
    label,
    hideLabel,
    value,
    required,
    readonly,
    disabled,
    onChange,
    onChangeOverride,
    onBlur,
    onFocus,
    autofocus,
    options,
    schema,
    uiSchema,
    formContext,
    type,
    rawErrors = [],
  } = props;

  const inputProps = getInputProps(schema, type, options);

  // Get semantic UI props if they exist
  const semanticProps =
    typeof (SemanticUITheme.templates?.BaseInputTemplate as any)
      ?.getSemanticProps === 'function'
      ? (SemanticUITheme.templates?.BaseInputTemplate as any).getSemanticProps({
          uiSchema,
          formContext,
          options,
        })
      : {};

  const handleChange = ({ target: { value: val } }: any) =>
    onChange(val === '' ? options.emptyValue : val);
  const handleBlur = () => onBlur && onBlur(id, value);
  const handleFocus = () => onFocus && onFocus(id, value);

  // Format the label
  const formattedLabel = label ? formatLabel(label) : undefined;

  return (
    <>
      <Form.Input
        id={id}
        name={id}
        placeholder={placeholder}
        {...inputProps}
        label={labelValue(formattedLabel || undefined, hideLabel, false)}
        required={required}
        autoFocus={autofocus}
        disabled={disabled || readonly}
        list={schema.examples ? examplesId(id) : undefined}
        {...semanticProps}
        value={value || value === 0 ? value : ''}
        error={rawErrors.length > 0}
        onChange={onChangeOverride || handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        aria-describedby={ariaDescribedByIds(id, !!schema.examples)}
      />
      {Array.isArray(schema.examples) && (
        <datalist id={examplesId(id)}>
          {schema.examples
            .concat(
              schema.default && !schema.examples.includes(schema.default)
                ? [schema.default]
                : [],
            )
            .map((example: any) => (
              <option key={example} value={example} aria-label={example} />
            ))}
        </datalist>
      )}
    </>
  );
};

/**
 * Custom TextareaWidget that formats labels to initial-case and removes
 * underscores.
 */
export const TextareaWidget = (props: WidgetProps) => {
  // Format the label before passing to the built-in widget
  const formattedProps = {
    ...props,
    label: props.label ? formatLabel(props.label) : props.label,
  };

  // Use the built-in `TextareaWidget` with formatted label
  const BuiltInTextareaWidget = SemanticUITheme.widgets?.TextareaWidget;

  if (!BuiltInTextareaWidget) {
    // Fallback if the built-in widget is not available (shouldn't happen
    return null;
  }

  return <BuiltInTextareaWidget {...formattedProps} />;
};

/**
 * Custom `SelectWidget` that formats dropdown labels to initial-case and
 * removes underscores. Also sets dropdowns to open upward to prevent clipping
 * in modals.
 */
export const SelectWidget = (props: WidgetProps) => {
  // Format the label before passing to the built-in widget
  const existingSemantic =
    typeof props.options?.semantic === 'object' &&
    props.options.semantic !== null
      ? props.options.semantic
      : {};

  const formattedProps = {
    ...props,
    label: props.label ? formatLabel(props.label) : props.label,
    options: {
      ...props.options,
      // Add Semantic UI props for the Dropdown component
      semantic: {
        ...existingSemantic,
        upward: true, // Open dropdown upward to prevent clipping in modals
      },
    },
  };

  // Use the built-in `SelectWidget` with formatted label
  const BuiltInSelectWidget = SemanticUITheme.widgets?.SelectWidget;

  if (!BuiltInSelectWidget) {
    // Fallback if built-in widget is not available
    return null;
  }

  return <BuiltInSelectWidget {...formattedProps} />;
};

/**
 * Custom CheckboxWidget that formats checkbox labels from camelCase to
 * human-readable format. Example: `emailAlerts` → `Email Alerts`
 */
export const CheckboxWidget = (props: WidgetProps) => {
  // Format the label before passing to the built-in widget
  const formattedProps = {
    ...props,
    label: props.label ? formatLabel(props.label) : props.label,
  };

  // Use the built-in `CheckboxWidget` with formatted label
  const BuiltInCheckboxWidget = SemanticUITheme.widgets?.CheckboxWidget;

  if (!BuiltInCheckboxWidget) {
    // Fallback if built-in widget is not available (again, you never know)
    return null;
  }

  return <BuiltInCheckboxWidget {...formattedProps} />;
};

/**
 * Custom field template for consistent Semantic UI styling
 * Note: Built-in Semantic UI widgets handle their own labels, so we don't
 * render labels here to avoid duplication. We only handle description, errors,
 * and help text. This template also detects nullable objects and delegates to
 * the `NullableObjectFieldTemplate`.
 */
export const FieldTemplate = (props: FieldTemplateProps) => {
  const {
    children,
    displayLabel,
    rawDescription,
    rawErrors,
    rawHelp,
    required,
    schema,
  } = props;

  // Check if this field is a nullable object and should use the
  // `NullableObjectFieldTemplate` instead.
  const isNullableObject = React.useMemo(() => {
    if (!schema) return false;
    // Check if schema has anyOf with a null type
    if (schema?.anyOf && Array.isArray(schema.anyOf)) {
      const hasNull = schema.anyOf.some(
        (subSchema: any) => subSchema?.type === 'null',
      );
      const hasObject = schema.anyOf.some(
        (subSchema: any) => subSchema?.type === 'object',
      );
      return hasNull && hasObject;
    }
    return false;
  }, [schema]);

  // If this is a nullable object, use the `NullableObjectFieldTemplate`
  if (isNullableObject) {
    return <NullableObjectFieldTemplate {...props} />;
  }

  // Otherwise, use the default field template
  return (
    <Form.Field
      required={required}
      error={rawErrors && rawErrors.length > 0}
      className="rjsf-field-wrapper"
    >
      {children}
      {rawDescription && displayLabel && (
        <small className="rjsf-field-description">{rawDescription}</small>
      )}
      {rawErrors && rawErrors.length > 0 && (
        <div className="ui pointing above red basic label rjsf-field-error-label">
          {rawErrors.join(', ')}
        </div>
      )}
      {rawHelp && <small className="rjsf-field-help">{rawHelp}</small>}
    </Form.Field>
  );
};

/**
 * Custom array field template for arrays of objects
 */
export const ArrayFieldTemplate = (props: any) => {
  const { items, canAdd, onAddClick, title } = props;
  // Disable remove button if there's only one item
  const canRemove = items && items.length > 1;

  return (
    <Form.Field>
      {title && (
        <>
          <Divider />
          <div className="field">
            <h4 className="ui header rjsf-array-field-header">
              {formatLabel(title)}
            </h4>
          </div>
        </>
      )}
      <div className="ui segments">
        {items?.map((element: any, index: number) => (
          <div key={element.key} className="ui segment rjsf-array-item-wrapper">
            {/* Ribbon label for the section */}
            <div className="ui ribbon label rjsf-array-item-ribbon">
              {formatLabel(title)
                ? `${formatLabel(title).replace(/s$/, '')} ${index + 1}`
                : `Item ${index + 1}`}
            </div>

            {/* Remove button positioned in top right - disabled if only one item */}
            {element.hasRemove && (
              <button
                type="button"
                className={`ui icon button negative mini rjsf-array-item-remove-button ${!canRemove ? 'disabled' : ''}`}
                onClick={
                  canRemove
                    ? element.onDropIndexClick(element.index)
                    : undefined
                }
                disabled={!canRemove}
                aria-label="Remove item"
                title={
                  !canRemove ? 'At least one item is required' : 'Remove item'
                }
              >
                <i className="trash icon" />
              </button>
            )}

            {/* Item content */}
            <div className="rjsf-array-item-content">{element.children}</div>
          </div>
        ))}
      </div>
      {canAdd && (
        <button
          type="button"
          className="ui button primary small"
          onClick={onAddClick}
        >
          <i className="plus icon" />
          Add Item
        </button>
      )}
    </Form.Field>
  );
};

/**
 * Custom object field template for nested objects
 */
export const ObjectFieldTemplate = (props: any) => {
  const { title, description, properties, schema, uiSchema, idSchema } = props;

  // Check if this object is an array item (has a numeric suffix like "Links-1")
  const isArrayItem = title && /^.*-\d+$/.test(title);

  // Check if this is the root level object (no title means it's the root)
  const isRootLevel = !title;

  // Determine nesting level by counting underscores in the ID schema path
  // Root objects have IDs like "root_components", nested objects have "root_components_button"
  const nestingLevel = idSchema?.$id
    ? (idSchema.$id.match(/_/g) || []).length
    : 0;
  const isFirstLevelObject = nestingLevel === 1;
  const isSecondLevelObject = nestingLevel === 2;

  return (
    <div>
      {/* Only show title if it's not an array item (to avoid duplication) */}
      {title && !isArrayItem && (
        <h4
          className={`ui header rjsf-object-field-header ${isFirstLevelObject ? 'rjsf-object-field-header-level-1' : ''} ${isSecondLevelObject ? 'rjsf-object-field-header-level-2' : ''}`}
        >
          {formatLabel(title)}
        </h4>
      )}
      {description && <p>{description}</p>}
      <div className="ui form">
        {properties?.map((prop: any, index: number) => {
          const currentPropIsObject =
            schema?.properties?.[prop.name]?.type === 'object' ||
            schema?.properties?.[prop.name]?.properties;

          // Check the previous property
          const prevProp = index > 0 ? properties[index - 1] : null;
          const prevPropIsObject =
            prevProp &&
            (schema?.properties?.[prevProp.name]?.type === 'object' ||
              schema?.properties?.[prevProp.name]?.properties);

          // Check if the NEXT property exists
          const nextProp = properties[index + 1];

          // At the root level:
          // 1. Show divider BEFORE an object if previous property exists and is
          // NOT an object
          const showDividerBefore =
            isRootLevel && currentPropIsObject && prevProp && !prevPropIsObject;

          // Check if this field has suppressAutoDivider in its UI schema
          const suppressAutoDivider =
            uiSchema?.[prop.name]?.['ui:options']?.suppressAutoDivider === true;

          // 2. Show divider AFTER an object if there is a next property, but
          // not if `suppressAutoDivider` is `true` (so custom fields handle
          // their own dividers)
          const showDividerAfter =
            isRootLevel &&
            currentPropIsObject &&
            nextProp &&
            !suppressAutoDivider;

          return (
            <div key={prop.name}>
              {showDividerBefore && <Divider />}
              {prop.content}
              {showDividerAfter && <Divider />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Create a hybrid theme that uses @rjsf/semantic-ui built-in widgets while
 * preserving our custom `ColorPickerWidget` and custom templates for special
 * styling (ribbon labels, formatted labels, dividers, and so forth).
 */
export const semanticUITheme = {
  // Use built-in widgets from @rjsf/semantic-ui as the base
  ...SemanticUITheme,
  widgets: {
    // Inherit all built-in Semantic UI widgets
    ...SemanticUITheme.widgets,
    // Add our custom `ColorPickerWidget` (not available in built-in theme)
    ColorPickerWidget,
    // Add our custom `TextareaWidget` with formatted labels
    TextareaWidget,
    // Add our custom `SelectWidget` with formatted labels
    SelectWidget,
    // Add our custom `CheckboxWidget` with formatted labels
    CheckboxWidget,
  },
  fields: {
    // Inherit all built-in Semantic UI fields
    ...SemanticUITheme.fields,
    // Add our custom `NullableObjectField` for nullable objects with
    // enable/disable toggles
    NullableObjectField,
  },
  templates: {
    // Use built-in templates as base
    ...SemanticUITheme.templates,
    // Override with our custom templates for special features:
    // - `BaseInputTemplate`: Formats labels to initial-case and removes
    // underscores
    // - `FieldTemplate`: Custom label formatting (underscores to spaces,
    // capitalization)
    // - `ArrayFieldTemplate`: Custom ribbon styling for array items
    // - `ObjectFieldTemplate`: Custom divider logic for nested objects
    // - `NullableObjectFieldTemplate`: Enables/disables nullable objects with
    // checkboxes
    BaseInputTemplate,
    FieldTemplate,
    ArrayFieldTemplate,
    ObjectFieldTemplate,
    NullableObjectFieldTemplate,
  },
};
